// Custom Metrics Management (S016)
import React, { useState, useEffect } from 'react';
import { apiClient } from '../lib/apiClient';

interface CustomMetric {
  id: string;
  name: string;
  formula: string;
  format: 'currency' | 'percentage' | 'number' | 'decimal';
  description?: string;
  value?: number;
  is_shared?: boolean;
  created_by?: string;
  usage_count?: number;
}

const AVAILABLE_VARIABLES = [
  { name: 'spend', description: 'Total ad spend' },
  { name: 'revenue', description: 'Total revenue' },
  { name: 'clicks', description: 'Total clicks' },
  { name: 'impressions', description: 'Total impressions' },
  { name: 'conversions', description: 'Total conversions' },
  { name: 'orders', description: 'Total orders' },
  { name: 'leads', description: 'Total leads' },
  { name: 'roas', description: 'Return on Ad Spend' },
  { name: 'ctr', description: 'Click-through Rate' },
  { name: 'cvr', description: 'Conversion Rate' },
];

const TEMPLATE_METRICS = [
  { name: 'Profit Margin %', formula: '(revenue - spend) / revenue * 100', format: 'percentage' as const, description: 'Percentage of revenue that is profit' },
  { name: 'Cost Per Lead', formula: 'spend / leads', format: 'currency' as const, description: 'Average cost to acquire one lead' },
  { name: 'ROI %', formula: '(revenue - spend) / spend * 100', format: 'percentage' as const, description: 'Return on investment percentage' },
  { name: 'Efficiency Score', formula: 'conversions / spend * 1000', format: 'number' as const, description: 'Conversions per $1000 spent' },
  { name: 'Profit Per Order', formula: '(revenue - spend) / orders', format: 'currency' as const, description: 'Average profit per order' },
];

export const CustomMetricsPage: React.FC = () => {
  const [metrics, setMetrics] = useState<CustomMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newMetric, setNewMetric] = useState({ 
    name: '', 
    formula: '', 
    format: 'number' as const,
    description: '' 
  });
  const [formulaValid, setFormulaValid] = useState<boolean | null>(null);
  const [formulaError, setFormulaError] = useState('');
  const [activeTab, setActiveTab] = useState<'my' | 'team' | 'templates'>('my');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setError(null);
      const response = await apiClient.get('/api/v1/custom-metrics');
      setMetrics(response.data.metrics || response.data || []);
    } catch (err: any) {
      console.error('Failed to load metrics:', err);
      setError('Failed to load metrics. Backend might not be running.');
      setMetrics([]);
    } finally {
      setLoading(false);
    }
  };

  const validateFormula = (formula: string) => {
    if (!formula.trim()) {
      setFormulaValid(null);
      setFormulaError('');
      return;
    }

    const lowerFormula = formula.toLowerCase();
    const variables = lowerFormula.match(/[a-z_]+/g) || [];
    const allowedFunctions = ['round', 'abs', 'min', 'max', 'sum', 'avg'];
    const allowedVariables = AVAILABLE_VARIABLES.map(v => v.name);
    
    const invalidVars = variables.filter(v => 
      !allowedFunctions.includes(v) && !allowedVariables.includes(v)
    );

    if (invalidVars.length > 0) {
      setFormulaValid(false);
      setFormulaError(`Unknown variables: ${invalidVars.join(', ')}`);
      return;
    }

    // Check for dangerous patterns
    if (/eval|import|exec|__/.test(lowerFormula)) {
      setFormulaValid(false);
      setFormulaError('Invalid formula: contains prohibited characters');
      return;
    }

    setFormulaValid(true);
    setFormulaError('');
  };

  const createMetric = async () => {
    if (!newMetric.name || !newMetric.formula) {
      alert('Please fill in name and formula');
      return;
    }
    try {
      await apiClient.post('/api/v1/custom-metrics', newMetric);
      setShowCreate(false);
      setNewMetric({ name: '', formula: '', format: 'number', description: '' });
      setFormulaValid(null);
      loadMetrics();
    } catch (err: any) {
      console.error('Failed to create metric:', err);
      alert(err.response?.data?.detail || 'Failed to create metric');
    }
  };

  const addFromTemplate = async (template: typeof TEMPLATE_METRICS[0]) => {
    try {
      await apiClient.post('/api/v1/custom-metrics', {
        name: template.name,
        formula: template.formula,
        format: template.format,
        description: template.description,
      });
      loadMetrics();
      setActiveTab('my');
    } catch (err: any) {
      console.error('Failed to use template:', err);
      alert(err.response?.data?.detail || 'Failed to add template metric');
    }
  };

  const deleteMetric = async (id: string) => {
    if (!confirm('Delete this metric?')) return;
    try {
      await apiClient.delete(`/api/v1/custom-metrics/${id}`);
      loadMetrics();
    } catch (err) {
      console.error('Failed to delete metric:', err);
    }
  };

  const toggleShare = async (id: string, currentlyShared: boolean) => {
    try {
      await apiClient.patch(`/api/v1/custom-metrics/${id}`, {
        is_shared: !currentlyShared
      });
      loadMetrics();
    } catch (err) {
      console.error('Failed to toggle share:', err);
    }
  };

  const insertVariable = (varName: string) => {
    setNewMetric({ ...newMetric, formula: newMetric.formula + varName });
    validateFormula(newMetric.formula + varName);
  };

  const formatValue = (value: number | undefined, format: string) => {
    if (value === undefined) return '—';
    switch (format) {
      case 'currency': return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
      case 'percentage': return `${value.toFixed(1)}%`;
      case 'decimal': return value.toFixed(2);
      default: return value.toLocaleString();
    }
  };

  const myMetrics = metrics.filter(m => !m.is_shared || m.created_by === 'current_user');
  const teamMetrics = metrics.filter(m => m.is_shared);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-black dark:text-white">Custom Metrics</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Create custom formulas to track unique KPIs
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-md"
          data-testid="create-metric-button"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Custom Metric
        </button>
      </div>

      {error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Create Metric Modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto mx-4" role="dialog">
            <h3 className="font-semibold text-xl mb-4 text-black dark:text-white">Create Custom Metric</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Left: Form */}
              <div className="md:col-span-2 space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Metric Name *
                  </label>
                  <input
                    type="text"
                    value={newMetric.name}
                    onChange={(e) => setNewMetric({ ...newMetric, name: e.target.value })}
                    placeholder="e.g., Cost Per Acquisition"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white"
                    aria-label="name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Formula *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={newMetric.formula}
                      onChange={(e) => {
                        setNewMetric({ ...newMetric, formula: e.target.value });
                        validateFormula(e.target.value);
                      }}
                      placeholder="e.g., spend / conversions"
                      className={`w-full px-4 py-2 border rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white ${
                        formulaValid === true ? 'border-green-500' : 
                        formulaValid === false ? 'border-red-500' : 
                        'border-gray-300 dark:border-gray-600'
                      }`}
                      aria-label="formula"
                    />
                    {formulaValid !== null && (
                      <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm ${
                        formulaValid ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {formulaValid ? '✓ Valid' : '✗ Error'}
                      </span>
                    )}
                  </div>
                  {formulaError && (
                    <p className="text-red-500 text-sm mt-1">{formulaError}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Format
                  </label>
                  <select
                    value={newMetric.format}
                    onChange={(e) => setNewMetric({ ...newMetric, format: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white"
                    aria-label="format"
                  >
                    <option value="number">Number</option>
                    <option value="currency">Currency ($)</option>
                    <option value="percentage">Percentage (%)</option>
                    <option value="decimal">Decimal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                    Description
                  </label>
                  <textarea
                    value={newMetric.description}
                    onChange={(e) => setNewMetric({ ...newMetric, description: e.target.value })}
                    placeholder="What does this metric measure?"
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-black dark:text-white"
                    rows={3}
                    aria-label="description"
                  />
                </div>
              </div>

              {/* Right: Available Variables */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                <h4 className="font-medium text-sm mb-3 text-gray-700 dark:text-gray-300">
                  Available Variables
                </h4>
                <div className="space-y-2">
                  {AVAILABLE_VARIABLES.map(v => (
                    <button
                      key={v.name}
                      onClick={() => insertVariable(v.name)}
                      className="w-full text-left px-3 py-2 text-sm bg-white dark:bg-gray-600 rounded hover:bg-blue-50 dark:hover:bg-gray-500 transition-colors"
                      title={v.description}
                    >
                      <span className="font-mono text-blue-600 dark:text-blue-400">{v.name}</span>
                      <span className="text-gray-500 dark:text-gray-400 text-xs block">{v.description}</span>
                    </button>
                  ))}
                </div>
                <h4 className="font-medium text-sm mt-4 mb-2 text-gray-700 dark:text-gray-300">
                  Operators
                </h4>
                <div className="flex flex-wrap gap-2">
                  {['+', '-', '*', '/', '(', ')'].map(op => (
                    <button
                      key={op}
                      onClick={() => insertVariable(` ${op} `)}
                      className="px-3 py-1 bg-white dark:bg-gray-600 rounded font-mono hover:bg-blue-50 dark:hover:bg-gray-500"
                    >
                      {op}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={() => {
                  setShowCreate(false);
                  setNewMetric({ name: '', formula: '', format: 'number', description: '' });
                  setFormulaValid(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={createMetric}
                disabled={!newMetric.name || !newMetric.formula || formulaValid === false}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Metric
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-4 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('my')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'my' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          My Metrics ({myMetrics.length})
        </button>
        <button
          onClick={() => setActiveTab('team')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'team' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Team Metrics ({teamMetrics.length})
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`pb-3 px-1 font-medium transition-colors ${
            activeTab === 'templates' 
              ? 'text-blue-600 border-b-2 border-blue-600' 
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Templates ({TEMPLATE_METRICS.length})
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : activeTab === 'templates' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {TEMPLATE_METRICS.map((template, idx) => (
            <div 
              key={idx} 
              className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm"
              data-testid="template-metric"
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-semibold text-black dark:text-white">{template.name}</h3>
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">Template</span>
              </div>
              <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded block mb-2 text-blue-600 dark:text-blue-400">
                {template.formula}
              </code>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{template.description}</p>
              <button
                onClick={() => addFromTemplate(template)}
                className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                + Use Template
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {(activeTab === 'my' ? myMetrics : teamMetrics).length === 0 ? (
            <div className="col-span-full text-center py-12 text-gray-500">
              {activeTab === 'my' 
                ? 'No custom metrics yet. Create your first one!' 
                : 'No shared team metrics yet.'}
            </div>
          ) : (
            (activeTab === 'my' ? myMetrics : teamMetrics).map(metric => (
              <div 
                key={metric.id} 
                className="bg-white dark:bg-gray-800 rounded-lg p-5 border border-gray-200 dark:border-gray-700 shadow-sm"
                data-testid="custom-metric-card"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-black dark:text-white" data-testid="metric-name">
                    {metric.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    {metric.is_shared && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Shared</span>
                    )}
                    <button
                      onClick={() => deleteMetric(metric.id)}
                      className="text-red-500 hover:text-red-700 text-lg font-bold"
                      title="Delete metric"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <code className="text-sm bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded block mb-2 text-blue-600 dark:text-blue-400">
                  {metric.formula}
                </code>
                {metric.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{metric.description}</p>
                )}
                {metric.value !== undefined && (
                  <div className="text-2xl font-bold text-black dark:text-white mb-3">
                    {formatValue(metric.value, metric.format)}
                  </div>
                )}
                <div className="flex justify-between items-center pt-3 border-t border-gray-100 dark:border-gray-700">
                  <span className="text-xs text-gray-400">
                    {metric.usage_count ? `Used ${metric.usage_count} times` : 'Not used yet'}
                  </span>
                  <button
                    onClick={() => toggleShare(metric.id, !!metric.is_shared)}
                    className="text-xs text-blue-600 hover:text-blue-700"
                  >
                    {metric.is_shared ? 'Unshare' : 'Share with team'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
